// Atmospheric scattering shader based on Nvidia GPU Gems 2 Chapter 16
// Creates a realistic glowing atmosphere effect using Fresnel, Rayleigh and Mie scattering

import { OKLAB_MIX_GLSL } from './oklab.js';
import { ECLIPSE_GLSL } from '../eclipse/geometry.js';
import { EARTH_RINGS_GLSL } from '../earthrings.js';

export const atmosphereVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const atmosphereFragmentShader = `
  uniform vec3 glowColor;
  uniform float intensity;
  uniform float power;

  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    // Calculate view direction
    vec3 viewDir = normalize(-vPosition);

    // Fresnel effect - stronger glow at edges
    float fresnel = pow(1.0 - abs(dot(viewDir, vNormal)), power);

    // Apply intensity
    float glowStrength = fresnel * intensity;

    // Output color with alpha based on glow strength
    gl_FragColor = vec4(glowColor, glowStrength);
  }
`;

// Earth-specific atmosphere with realistic Rayleigh and Mie scattering
export const earthAtmosphereVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vPlanetViewCenter;
  varying vec3 vWorldPosition;
  varying vec3 vWorldNormal;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vWorldNormal = normalize((modelMatrix * vec4(normal, 0.0)).xyz);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    // The model-view translation is computed on the CPU in double precision.
    // Keep limb rays in this small frame instead of subtracting interpolated
    // solar-system positions in the fragment shader (map Earth is ~0.21 units).
    vPlanetViewCenter = modelViewMatrix[3].xyz;
    vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// OKLab/OKLCh perceptual mixing helper functions are imported at the
// top of this file from ./oklab.js and injected into each fragment
// shader via the OKLAB_MIX_GLSL template literal.

export const earthAtmosphereFragmentShader = `
  ${OKLAB_MIX_GLSL}
  uniform vec3 sunPosition;
  uniform vec3 atmosphereColor;
  uniform float atmosphereStrength;
  // 0 leaves the shell as it is; 1 hands it entirely to the viewer's
  // low-orbit sky dome (earthclose.js), which draws the air close in.
  uniform float uShellCloseFade;
  uniform float earthDensityLimb; // Earth: exponential outer-air falloff
  // Rayleigh tint — Earth's sky-blue by default. Venus dials this
  // toward zero (sulphuric haze is Mie-dominated), Mars shifts it to
  // dusty red, Titan to amber. Passing the tint as a uniform lets a
  // single shader cover every atmosphere without the other planets
  // inheriting Earth's blue.
  uniform vec3 rayleighTint;
  uniform float rayleighStrengthMul;
  // Limb brightness tint also parameterised so non-Earth bodies don't
  // get Earth's cool-blue highlight on the sunlit rim.
  uniform vec3 limbTint;
  // Terminator twilight colour. Earth's is amber-orange (low-sun
  // Rayleigh stripping), but Mars's suspended iron-oxide dust
  // forward-scatters BLUE preferentially — the famous blue Martian
  // sunset in Curiosity / Spirit imagery — and Venus's sulphuric
  // deck goes pale cream. Wired per body from main.js; Earth's
  // value matches the previous hardcoded vec3(1.0, 0.5, 0.3).
  uniform vec3 twilightTint;
  // Backlit forward-scatter ring. Venus observed near inferior
  // conjunction shows sunlight scattered through its thick
  // atmosphere ALL the way around the night-side limb — the
  // crescent's horns extend into a complete luminous ring. Only
  // dense-atmosphere bodies set this above 0.
  uniform float forwardRingStrength;
  // Polar-aurora tint. Bakes a soft green colour into the atmosphere
  // halo at high northern latitudes — a tasteful aurora hint that
  // doesn't need a separate particle system. Drives a faint shimmer
  // via the time uniform so the green reads as living aurora rather
  // than a static gradient. Only the earth material sets auroraStrength
  // > 0; other planets inherit 0 and skip the branch entirely.
  uniform vec3 auroraTint;
  uniform float auroraStrength;
  // World-space spin axis of the planet, wired from main.js. The
  // atmosphere mesh sits outside the axial-tilt group, so deriving
  // latitude from vWorldNormal.y would centre the auroral oval on the
  // untilted world axis — ~23° off Earth's real poles (and the aurora
  // curtains, which do follow the tilt). Defensive fallback to +Y when
  // unwired keeps non-Earth atmospheres unchanged.
  uniform vec3 uSpinAxis;
  uniform float time;
  // Ray-traced planet shadow on the atmosphere itself. For every
  // atmosphere fragment we cast a ray toward the sun and test if it
  // hits the planet sphere — if yes, the fragment is in the planet's
  // shadow cone and direct sunlight can't reach it, so the
  // atmosphere there should be dark (only airglow). Without this,
  // the night-side atmosphere fades only by dot(N, sunDir) which is
  // wrong: there's an actual shadow cone behind the planet that
  // wraps around the limb, visible in DSCOVR and lunar-eclipse
  // photography. This is the Hillaire-2020 single biggest realism
  // win that doesn't need a full LUT pipeline.
  uniform vec3 planetCenter;
  uniform float planetRadius;
  // Atmosphere top radius — used by Hillaire-style chord-length
  // scattering: the view ray's actual path through the atmospheric
  // shell is computed by ray-sphere intersection, converted to "air
  // masses" relative to vertical thickness, and Beer-Lambert extinction
  // is applied per-wavelength. Long chord at the limb strips blue,
  // leaves the warm wavelengths — the physically correct reason the
  // limb glow turns orange near the terminator on real Earth photos.
  uniform float atmosphereRadius;
  // Moon eclipse shadow uniforms. moonShadowCount is the number of
  // active moons (0-8). For each i, moonShadowPositions[i] is the
  // moon's world position and moonShadowRadii[i] is its physical
  // radius. Used in moonEclipseFactor() to dim the atmosphere
  // fragments that sit inside any moon's shadow column toward the
  // sun, so the atmosphere ring properly darkens during transits
  // / solar eclipses viewed from orbit (Heckel 2026, article on
  // atmospheric rendering).
  uniform int moonShadowCount;
  uniform vec3 moonShadowPositions[8];
  uniform float moonShadowRadii[8];
  // The Earth's shell: the Moon's shadow from the real geometry, the same
  // uniforms as the surface (src/shaders/earth.js). Other planets leave
  // uEclipseOn at 0 and keep the moon loop below.
  uniform float uEclipseOn;
  uniform vec3 uEclipseEarthCenter;
  uniform vec3 uEclipseMoonKm;
  uniform vec3 uEclipseSunKm;
  ${ECLIPSE_GLSL}
  ${EARTH_RINGS_GLSL}

  varying vec3 vNormal;
  varying vec3 vPosition;
  varying vec3 vWorldPosition;
  varying vec3 vWorldNormal;
  varying vec3 vPlanetViewCenter;

  // Rayleigh scattering coefficients (wavelength dependent)
  // Blue scatters more than red
  const vec3 rayleighCoeff = vec3(5.5e-6, 13.0e-6, 22.4e-6);

  // Mie scattering coefficient (wavelength independent)
  const float mieCoeff = 21e-6;

  // Mie phase function: Cornette-Shanks formulation. More accurate
  // than plain Henyey-Greenstein because it includes the (1+mu^2)
  // numerator term, which gives a slightly brighter, more directional
  // forward scatter near the sun (matches what Hillaire 2020 and
  // Heckel 2026 use). Result: tighter sun halo, less side-scatter
  // bleed, more physically grounded ratio against Rayleigh.
  float miePhase(float cosTheta, float g) {
    float g2 = g * g;
    float num = 3.0 * (1.0 - g2) * (1.0 + cosTheta * cosTheta);
    float den = 8.0 * 3.14159265 * (2.0 + g2) *
                pow(max(1.0 + g2 - 2.0 * g * cosTheta, 1e-4), 1.5);
    return num / den;
  }

  // Rayleigh phase function with proper 1/(16*PI) normalization (the
  // physically correct constant per Bruneton / Hillaire / Heckel).
  // Previously we used 0.75 = 3/4, which is the form WITHOUT the
  // 1/(4*PI) "energy per unit solid angle" factor; it's a constant
  // 4*PI too bright. The 4PI_COMP scale below restores the previous
  // visual brightness so per-body rayleighStrengthMul stays valid,
  // but the relative balance between Rayleigh and the now-correctly-
  // normalized Mie is improved.
  float rayleighPhase(float cosTheta) {
    return (3.0 / (16.0 * 3.14159265)) * (1.0 + cosTheta * cosTheta);
  }
  const float PHASE_4PI_COMP = 12.566370614;

  // Chord length through the atmospheric shell along the view ray
  // from the camera to this fragment. Hillaire 2020's core idea:
  // path-length matters per-wavelength. Sphere-line intersection
  // against the atmosphere outer radius gives entry/exit; planet
  // surface intersection (if the ray hits the body) caps the chord.
  // Returns chord length in scene units, 0 if no atmosphere is
  // traversed (camera inside, ray misses, etc).
  float atmosphereChord() {
    if (atmosphereRadius <= 0.0) return 0.0;
    vec3 viewRay = vPosition;
    float fragDist = length(viewRay);
    if (fragDist < 1e-5) return 0.0;
    vec3 viewDir = viewRay / fragDist;

    vec3 oc = -vPlanetViewCenter;
    float b = dot(oc, viewDir);
    vec3 impact = cross(oc, viewDir);
    float impactSq = dot(impact, impact);
    float aDisc = atmosphereRadius * atmosphereRadius - impactSq;
    if (aDisc < 0.0) return 0.0;
    float aSqrt = sqrt(aDisc);
    float tEnter = max(-b - aSqrt, 0.0);
    float tAtmoExit = -b + aSqrt;

    // Check if the planet body occludes the back wall — chord ends
    // wherever the surface is hit before the atmosphere exits.
    float pDisc = planetRadius * planetRadius - impactSq;
    float tEnd = tAtmoExit;
    if (pDisc >= 0.0) {
      float tPlanet = -b - sqrt(pDisc);
      if (tPlanet > tEnter) tEnd = min(tEnd, tPlanet);
    }
    // Also clamp to the actual fragment distance — light coming from
    // farther than the fragment hasn't reached us yet.
    tEnd = min(tEnd, fragDist);
    return max(0.0, tEnd - tEnter);
  }

  // Moon eclipse occlusion. For each registered moon, test if its
  // shadow column (parallel to the sun direction from the moon)
  // intersects the atmosphere fragment. Returns 0.0 when no moon
  // blocks the sun from this fragment's viewpoint, up to 1.0 when
  // a moon's umbra fully covers the sun. Geometry mirrors the
  // surface shader's eclipse test so the atmospheric shadow and
  // surface shadow stay coincident at the limb during transits.
  float moonEclipseFactor(vec3 fragPos, vec3 sunDirNorm) {
    if (uEclipseOn > 0.5) {
      // The shell over a ground point shares its shadow.
      vec3 p = normalize(fragPos - uEclipseEarthCenter) * ECL_R_EARTH;
      return eclSunHidden(p, uEclipseSunKm, uEclipseMoonKm, ECL_R_MOON);
    }
    float occlusion = 0.0;
    for (int i = 0; i < 8; i++) {
      if (i >= moonShadowCount) break;
      vec3 moonPos = moonShadowPositions[i];
      float moonRad = moonShadowRadii[i];
      // Shadow axis points AWAY from sun (from moon outward).
      vec3 shadowAxis = -sunDirNorm;
      vec3 moonToFrag = fragPos - moonPos;
      // t = how far along the shadow axis the fragment sits. Negative
      // means the fragment is between the sun and moon (no eclipse).
      float t = dot(moonToFrag, shadowAxis);
      if (t <= 0.0) continue;
      // Distance from the fragment to the shadow axis. If less than
      // the moon's apparent radius, the fragment is in the umbra/
      // penumbra zone.
      vec3 perp = moonToFrag - shadowAxis * t;
      float perpDist = length(perp);
      // Two-band soft shadow: tight umbra inside half the radius,
      // softer penumbra ring extending to ~3x for the atmospheric
      // limb to dim gradually rather than hard-clip.
      float umbra = 1.0 - smoothstep(moonRad * 0.4, moonRad * 0.85, perpDist);
      float penumbra = 1.0 - smoothstep(moonRad * 0.85, moonRad * 3.0, perpDist);
      float local = umbra * 0.80 + (penumbra - umbra) * 0.30;
      occlusion = max(occlusion, clamp(local, 0.0, 1.0));
    }
    return occlusion;
  }

  // Sun-path transmittance through the atmosphere at this fragment.
  // The proper Hillaire 2020 approach is to ray-march from each
  // atmosphere sample toward the sun, accumulating optical depth at
  // each step. Since our atmosphere is a thin shell with effectively
  // uniform density, the chord-length trick we already use for the
  // view ray applies equally to the sun ray: distance from fragment
  // to atmosphere top in the sun direction, divided by vertical
  // thickness, gives the sun-side air mass. Beer-Lambert per-
  // wavelength extinction on this air mass is what shifts the
  // scattered light from white through gold to deep red as the sun
  // approaches the horizon at this fragment's location. Before this
  // pass, only the view-ray chord was attenuated, so the limb at
  // sunset glowed with the wrong color.
  vec3 sunPathTransmittance(vec3 fragPos, vec3 sunDirNorm) {
    if (atmosphereRadius <= 0.0) return vec3(1.0);
    vec3 oc = fragPos - planetCenter;
    float b = dot(oc, sunDirNorm);
    float c = dot(oc, oc) - atmosphereRadius * atmosphereRadius;
    float disc = b * b - c;
    if (disc < 0.0) return vec3(1.0);
    // Sun is outside the atmosphere; exit point is at the FAR root.
    float sunChord = max(-b + sqrt(disc), 0.0);
    float verticalThickness = max(atmosphereRadius - planetRadius, 1e-4);
    float sunAirMass = sunChord / verticalThickness;
    // Same per-wavelength coefficients as the view-ray extinction so
    // the in-coming and out-going attenuation are consistent and the
    // result reads as a single coherent Beer-Lambert response.
    vec3 sunRayExt = vec3(0.10, 0.30, 0.85) * sunAirMass * 0.18;
    vec3 sunOzExt = vec3(0.07, 0.18, 0.01) * sunAirMass * 0.12 * rayleighStrengthMul;
    return exp(-(sunRayExt + sunOzExt));
  }

  // Ray-traced planet-shadow test for the atmosphere. Casts a ray
  // from the fragment toward the sun and tests if it hits the planet
  // sphere. Returns 1.0 when the fragment is in full shadow, 0.0 in
  // full sunlight, with a soft penumbra band based on how close the
  // grazing ray comes to the planet's silhouette (cheap analytic
  // softening that approximates the sun's ~0.5° angular size).
  float planetShadowFactor(vec3 fragPos, vec3 sunDir) {
    if (planetRadius <= 0.0) return 0.0;
    vec3 oc = fragPos - planetCenter;
    float b = dot(oc, sunDir);
    float c = dot(oc, oc) - planetRadius * planetRadius;
    // Closest approach of the ray (fragPos + t * sunDir) to planet
    // center, squared. = c - b*b when t = -b is the closest point.
    float closestApproachSq = c - b * b;
    // If the closest-approach point is behind the fragment (b < 0)
    // and the ray misses the planet, no shadow.
    if (b < 0.0 && closestApproachSq > 0.0) return 0.0;
    // Hard shadow: ray hits the planet (closestApproachSq < radius²
    // AND the intersection is in front of the fragment, b > 0 OR
    // the fragment is inside the planet which shouldn't happen here).
    if (b > 0.0 && closestApproachSq < 0.0) {
      // Inside the umbra cone. Soft penumbra near the silhouette
      // based on how close the ray skims the planet edge.
      float penumbra = 1.0 - smoothstep(
        -planetRadius * planetRadius * 0.0,
        -planetRadius * planetRadius * 0.04,
        closestApproachSq
      );
      return penumbra;
    }
    return 0.0;
  }

  void main() {
    vec3 viewDir = normalize(-vPosition);
    vec3 sunDir = normalize(sunPosition - vWorldPosition);

    // Cosine of angle between view and sun
    float cosTheta = dot(viewDir, sunDir);

    // === RAY-TRACED PLANET SHADOW ===
    // Test if direct sunlight can reach this atmosphere fragment.
    // The result drives a multiplicative dimming of all scattering
    // contributions below — fragments inside Earth's shadow cone
    // are dark because the sun is geometrically blocked, not just
    // because the surface normal points away.
    float shadowed = planetShadowFactor(vWorldPosition, sunDir);
    // Moon eclipse occlusion: a moon transiting the sun dims the
    // atmospheric column behind it. Combined multiplicatively with
    // the planet shadow so a full lunar transit during a planet
    // shadow stacks correctly without going negative.
    float moonOcc = moonEclipseFactor(vWorldPosition, sunDir);
    float litFactor = (1.0 - shadowed * 0.92) * (1.0 - moonOcc * 0.85);
    // The shadow of the Earth's rings (?whatif=rings, src/earthrings.js).
    litFactor *= 1.0 - (1.0 - earthRingLight(vWorldPosition)) * 0.92;
    litFactor = max(litFactor, 0.05); // tiny airglow floor

    // Fresnel falloff: tight at orbital viewing distance (thin limb
    // band matching the visible ~80 km atmosphere from far away),
    // softer at ISS-close range so the atmosphere reads as a
    // hazier band that fades smoothly into space instead of a
    // sharp edge. closeFactor ramps 0->1 as the camera approaches
    // the planet between 2.0x and 5.0x planet radii; at that range
    // the chord through atmosphere along the view ray is much
    // longer, so the atmosphere should naturally read thicker and
    // hazier (per Heckel / Hillaire references) rather than the
    // tight pow(6) ring that looks right from outer-orbit views.
    float edgeFactor = 1.0 - abs(dot(viewDir, vNormal));
    float distToPlanet = length(vPlanetViewCenter);
    // Two-stage close-factor ramp. The outer stage (5x → 2x radii)
    // gradually softens the fresnel from the orbital-view value
    // down to a mid-close value. The inner stage (1.5x → 1.05x —
    // ISS altitude is roughly 1.065x Earth's radius) pushes
    // hazinness further so when the camera is RIGHT at the
    // station's altitude, the atmosphere reads as a thick diffuse
    // veil like in real ISS imagery rather than a tight bright
    // ring. The two factors combine multiplicatively into the
    // alpha boost so the transition is smooth as the camera dollies.
    float closeFactor = 1.0 - smoothstep(planetRadius * 2.0, planetRadius * 5.0, distToPlanet);
    float issLevelFactor = 1.0 - smoothstep(planetRadius * 1.05, planetRadius * 1.5, distToPlanet);
    // fresnelPow now stays in a tight band: 3.5 from orbit (slightly
    // softer than the original 6.0 — gives the diffuse feathered
    // edge the user liked from ISS-close, applied as the baseline
    // so Earth reads more realistically from any distance), 2.6
    // mid-close, 1.8 at ISS-altitude. The diffuse character is
    // now the default; the close-range stages just widen it
    // further as the chord through atmosphere grows.
    float fresnelPow = mix(mix(3.5, 2.6, closeFactor), 1.8, issLevelFactor);
    float fresnel = pow(edgeFactor, fresnelPow);
    // Close-range haze boost. At ISS-altitude the boost is moderate
    // (~2x alpha vs orbital) — wider band, but not so bright that
    // the atmosphere reads as a glowing halo. Kept lower than the
    // initial pass since user feedback was "too bright"; the
    // diffuse character now comes from the softened fresnel and
    // the outer alpha taper rather than from raw luminance.
    float closeHazeBoost = 1.0 + closeFactor * 0.55 + issLevelFactor * 0.85;

    // Polar-aurora green at high northern (and a softer southern)
    // latitudes. Latitude comes from the surface normal projected onto
    // the planet's spin axis — north = +1, equator = 0, south = -1 —
    // so the oval tracks the tilted poles, not world +Y. Northern lobe
    // is the dominant one; the southern aurora gets a gentler tint.
    // Auroral oval band — RING shape centred at ~70° latitude
    // (lat ≈ 0.94). Real auroras live at 65-78° magnetic
    // latitude, not at the geographic pole and not across the whole
    // high hemisphere. Earlier (0.40 → 0.85) was a polar CAP that
    // covered everything down to ~58° latitude — far too much area,
    // which made the whole northern atmosphere read green.
    //   rise:   smoothstep(0.78, 0.92, lat)  — band rises around 51°-67° lat
    //   inner:  smoothstep(0.97, 1.0, lat)   — falls off at the pole itself
    // Subtracting them gives a ring with peak ~70° lat.
    vec3 spinAxis = length(uSpinAxis) > 0.5 ? normalize(uSpinAxis) : vec3(0.0, 1.0, 0.0);
    float lat = dot(normalize(vWorldNormal), spinAxis);
    // Equatorward edge widened (0.78→0.92 became 0.72→0.94) so the
    // oval's lower boundary blends in gradually instead of arriving
    // as a hard latitude line.
    float northLobe = smoothstep(0.72, 0.94, lat) - smoothstep(0.97, 1.0, lat);
    float southLobe = (smoothstep(0.72, 0.94, -lat) - smoothstep(0.97, 1.0, -lat)) * 0.75;
    float auroraLobe = max(northLobe, southLobe) * auroraStrength;
    // Time-only pulse — pure temporal breathing with no spatial
    // pattern. Earlier the shimmer also varied with longitude (×6) and
    // latitude (×12) and dipped down to 0.56, which painted six bright
    // stripes around the pole plus a 12-cycle phase across latitude —
    // the user saw those dark stripes as "hairlines". Now: gentle ±5%
    // temporal breathing only, no spatial modulation, never goes dim
    // enough to render as a dark band.
    float shimmer = 0.97 + 0.03 * sin(time * 0.6);
    // Rayleigh tint stays PURE — no blend into aurora green here.
    // The earlier OKLCh mix rotated through teal at any latitude
    // where auroraLobe > 0, making Earth's atmosphere read teal
    // instead of sky blue. The dedicated aurora pass below adds the
    // polar green as its own additive contribution, leaving the
    // rest of the atmosphere in its native Rayleigh blue.
    vec3 effectiveRayleighTint = rayleighTint;

    // === HILLAIRE CHORD-LENGTH EXTINCTION ===
    // Compute the actual view-ray path length through the atmosphere
    // and convert to "air masses" relative to the vertical shell
    // thickness. Long chords at the limb mean blue is scattered out
    // disproportionately (Rayleigh ∝ 1/λ⁴) leaving warm wavelengths.
    // This is what makes the real Earth limb glow turn red-orange
    // near the terminator on Apollo/ISS imagery, not a painted
    // twilight smoothstep.
    float chord = atmosphereChord();
    float verticalThickness = max(atmosphereRadius - planetRadius, 1e-4);
    float airMass = chord / verticalThickness;
    // Beer-Lambert per-wavelength extinction. Combines Rayleigh
    // scattering (1/lambda^4 means blue is removed at long path
    // lengths, leaving warm wavelengths) with ozone absorption
    // (Chappuis band peaks around 600 nm, removing green/yellow
    // more than blue). Ozone is what gives a real Earth sunset its
    // characteristic deep purple/violet color near the horizon,
    // not just orange-red. Without ozone-in-transmittance the limb
    // never shifts past the rayleigh-strip color. Coefficients are
    // empirical-but-shaped by Hillaire 2020: ozone vec3(0.07, 0.18,
    // 0.01) gates green hardest, leaves blue mostly intact.
    vec3 rayleighExt = vec3(0.10, 0.30, 0.85) * airMass * 0.18;
    vec3 ozoneExt = vec3(0.07, 0.18, 0.01) * airMass * 0.12 * rayleighStrengthMul;
    vec3 perWavelengthExt = exp(-(rayleighExt + ozoneExt));

    // Sun-path transmittance at this fragment. Combined multiplicatively
    // with the view-path extinction so the scattered light is double-
    // attenuated (proper Hillaire 2020 formulation): once for sunlight
    // traveling INTO the atmosphere to reach this fragment, again for
    // scattered light traveling OUT to the camera. The combined effect
    // is what gives the limb at sunset its deep red-orange gradient
    // (long sun path strips blue twice over) versus a cleaner blue
    // halo at the zenith (short sun path, preserved spectrum).
    vec3 sunTransmittance = sunPathTransmittance(vWorldPosition, sunDir);

    // Rayleigh scattering - tinted per planet, scaled by global
    // multiplier. Modulated by ray-traced planet-shadow factor and
    // wavelength-dependent extinction so the limb actually reddens
    // physically rather than via smoothstep cheats. PHASE_4PI_COMP
    // compensates for the new physically-normalized Rayleigh phase
    // function so per-body rayleighStrengthMul values keep their
    // previous visual weight.
    float rayPhase = rayleighPhase(cosTheta);
    vec3 rayleighColor = effectiveRayleighTint * rayPhase * PHASE_4PI_COMP * rayleighStrengthMul * litFactor * perWavelengthExt * sunTransmittance;

    // Mie scattering - forward scattering towards sun (haze/glow
    // around sun). Cornette-Shanks formulation: more directional than
    // plain Henyey-Greenstein, tighter sun halo. 0.2 strength
    // preserved from the previous tuning.
    float mieStrength = miePhase(cosTheta, 0.76);
    vec3 mieColor = vec3(1.0, 0.95, 0.8) * mieStrength * 0.2 * litFactor * sunTransmittance;

    // Day/night factor aligned with the surface shader's tight window
    // so the shell halo tracks the same axis-derived terminator the
    // ground uses — no visible mismatch at the dusk line.
    float sunDot = dot(vWorldNormal, sunDir);
    float dayFactor = smoothstep(-0.03, 0.2, sunDot);

    // Twilight/sunset colors at terminator - softer transition.
    // Tint is per-body (Earth amber, Mars dust-scatter blue, Venus
    // cream); defensive fallback to Earth's amber when unwired.
    vec3 twiTint = dot(twilightTint, vec3(1.0)) > 0.001 ? twilightTint : vec3(1.0, 0.5, 0.3);
    float twilightFactor = smoothstep(-0.05, 0.05, sunDot) * smoothstep(0.25, 0.0, sunDot);
    vec3 twilightColor = twiTint * twilightFactor * 0.5;

    // Multi-scattering approximation. Real atmospheres have light
    // bouncing between molecules multiple times before reaching the
    // viewer, which produces an ambient atmospheric glow that does
    // not depend on direct sun-ray geometry. Hillaire 2020 models
    // this with a dedicated LUT; we approximate with a constant term
    // proportional to the local rayleigh density and tint. Visible
    // payoff: the dark hemisphere of atmospheric bodies glows
    // subtly blue (Earth) / orange (Mars), like ISS night-side
    // imagery shows the auroral oval against a faintly luminous sky
    // rather than pure black. Strength gated by atmosphereStrength
    // so non-atmospheric bodies (lookalike outer-system rocks that
    // share the shader) get nothing.
    vec3 multiScatter = effectiveRayleighTint * rayleighStrengthMul * atmosphereStrength * 0.10;

    // Combine all scattering effects
    vec3 scatterColor = rayleighColor * dayFactor + mieColor + twilightColor + multiScatter;
    // OKLCh blend of the base atmosphere tint with the scattered
    // light contribution (Bujack 2025, Ottosson 2020). Linear sRGB
    // mix of the deep-blue base into the saturated Rayleigh-plus-Mie
    // scatter slurry runs the daytime sky color through a desaturated
    // grey-blue intermediate, which is why pure-RGB atmosphere shells
    // tend to look flat-pastel rather than vivid. The perceptual
    // hue arc keeps the daylit sky band saturated, which matches the
    // brilliant cyan-blue ISS photographs show on the lit hemisphere.
    vec3 finalColor = oklchMix(atmosphereColor, scatterColor, 0.5);

    // Aurora layer — runs as its own pass with its own colour AND
    // alpha contribution, so the auroral oval glows on the polar
    // limb regardless of how dim the surrounding atmosphere alpha
    // is. The rayleigh-tinted aurora colour from the rayleigh
    // branch goes to zero on the night side because of the dayFactor
    // gate, so without this dedicated pass the polar green
    // disappeared exactly where it should be most visible. OKLCh-
    // mixed (Bujack 2025) from atmosphere base into pure aurora tint
    // with the per-frame shimmer applied.
    float auroraAlpha = 0.0;
    if (auroraStrength > 0.001) {
      vec3 auroraOnly = max(
        oklchMix(atmosphereColor, auroraTint * shimmer, 1.0),
        vec3(0.0)
      );
      // Night-side feather, pushed DEEP into the dark hemisphere so
      // the blend is soft without re-triggering the terminator
      // z-fight artifact. The old [-0.05, -0.10] feather (0.05 wide)
      // read as a hard cutoff line — the boundary the user flagged.
      // Widening it AT the terminator previously beaded into dashed
      // hair arcs because the atmosphere shell z-fights Earth's
      // surface there. The fix: keep the near edge a touch past the
      // terminator (-0.06, so the immediate z-fight zone at sunDot≈0
      // stays at zero aurora alpha) and extend the far edge to -0.24,
      // giving an 0.18-wide feather that lives entirely on the clean
      // night side where the shell is unambiguously in front. The
      // result is a seamless fade-in instead of a hard green line.
      float auroraVisibility = auroraLobe * smoothstep(-0.06, -0.24, sunDot);
      // Colour contribution into finalColor. The 1.4× multiplier
      // makes the green register clearly through additive blending
      // against bloom; OKLCh keeps the hue stable so the boost
      // doesn't shift it.
      finalColor += auroraOnly * auroraVisibility * 1.4;
      // Dedicated alpha contribution. Spread across the polar
      // shell — strongest at the limb (fresnel-weighted, like a
      // real auroral arc seen edge-on), but with a base term so
      // the oval is still visible when the camera looks down at
      // the pole from above.
      auroraAlpha = auroraVisibility * (0.25 + 0.75 * fresnel) * atmosphereStrength;
    }

    // Softer limb brightness
    float limbBrightness = fresnel * max(sunDot, 0.0) * 0.3;
    finalColor += limbTint * limbBrightness;

    // Diffuse outer haze: wide low-power fresnel that paints a soft
    // glow across the entire silhouette region rather than a tight
    // line. Pairs with the HDR brightness boost below so the bloom
    // pass picks the silhouette up and feathers it OUT past the mesh
    // edge, killing the hard cut between atmosphere and space. The
    // 1.4 exponent gives a very wide falloff; multiplied by a small
    // alpha contribution, it just nudges the band wider without
    // washing the disc with extra blue.
    //
    // At ISS-altitude the haze contribution amplifies considerably
    // (issLevelFactor pushes the alpha multiplier ~5x and tints
    // toward white-blue) so the day-side limb reads as a thick
    // luminous atmospheric column the way real ISS photography
    // captures it — bright cyan-white stratification fading
    // upward into the deeper Rayleigh blue.
    // Wide soft-haze exponent stays roughly constant across all
    // viewing distances so the diffuse character reads from any
    // zoom. Slight broadening at ISS range keeps the dramatic
    // close-up but the orbital baseline (1.1) is already wide
    // enough that Earth's limb fades smoothly into space rather
    // than terminating in a hard ring.
    float softHaze = pow(edgeFactor, mix(1.1, 0.9, issLevelFactor));
    vec3 hazeTint = effectiveRayleighTint * rayleighStrengthMul;
    // At ISS range, lift the haze toward a softer cyan-white so the
    // low-altitude scattering layer carries a hint of atmospheric
    // bloom. The orbital tint is the rayleigh blue (slight bias),
    // ramping toward cyan-white at close range.
    vec3 issHazeTint = mix(hazeTint, vec3(0.78, 0.88, 0.98), 0.30);
    vec3 hazeContrib = mix(hazeTint, issHazeTint, issLevelFactor);
    // Haze brightness baseline lifted to 0.14 (was 0.08) so the
    // diffuse veil is visible from orbital distance too, not just
    // from ISS-close. Still capped at 0.20 at ISS to avoid wash-out.
    float hazeMul = mix(0.14, 0.20, issLevelFactor);
    finalColor += hazeContrib * softHaze * hazeMul * dayFactor * litFactor;

    // HDR silhouette glow: punch the brightness near the visible edge
    // above the bloom threshold so the post-process bloom blurs the
    // rim outward into space. The wider rim band is now the default
    // (smoothstep 0.66 → 1.0 from orbit, 0.58 → 1.0 at ISS) since
    // user testing showed the diffuse feathered edge reads as a
    // more realistic Earth at any distance.
    float rimGlowWidth = mix(0.66, 0.58, issLevelFactor);
    float rimGlow = smoothstep(rimGlowWidth, 1.0, edgeFactor);
    vec3 rimColor = mix(effectiveRayleighTint, vec3(1.0), 0.35);
    float rimMul = mix(0.72, 0.9, issLevelFactor);
    // Break the perfectly-even rim. Real atmospheric limb brightness
    // varies around the circumference with weather, cloud tops and
    // scattering density — a uniform ring is a CGI tell. A couple of
    // low-frequency sines of the world-space normal undulate the rim
    // (~±18%), drifting slowly in time so it reads as a living
    // atmosphere rather than a stamped halo.
    vec3 rimN = normalize(vWorldNormal);
    float rimVar = 0.82 + 0.18 * (0.5 + 0.5 * sin(rimN.x * 7.0 + rimN.y * 9.0 + rimN.z * 5.0 + time * 0.15));
    finalColor += rimColor * rimGlow * dayFactor * litFactor * smoothstep(0.0, 0.5, rayleighStrengthMul) * rimMul * rimVar;

    // Far-edge diffusion is now an always-on contribution, not gated
    // to ISS-close. The very wide low-power fresnel tints the
    // silhouette hemisphere with a soft veil that ramps up with
    // proximity but is visible from any distance — this is what
    // gives the diffuse atmospheric stratification a real-Earth
    // photo gets at every zoom.
    float farHaze = pow(edgeFactor, 0.5);
    vec3 farHazeTint = vec3(0.72, 0.85, 0.98);
    float farHazeMul = mix(0.04, 0.10, issLevelFactor);
    finalColor += farHazeTint * farHaze * farHazeMul * dayFactor * litFactor * smoothstep(0.0, 0.5, rayleighStrengthMul);

    // Ozone violet ring above the orange day-limb glow band. Real
    // Earth-from-orbit imagery shows a clear horizon stratification:
    // orange (low-altitude scatter) → violet (ozone absorption peak
    // around 30 km altitude) → blue (Rayleigh) → black space.
    // Gated by rayleighStrengthMul so only Earth gets the ozone ring
    // (other planets without an ozone layer skip this contribution).
    // The ring sits between edgeFactor^4 (lower band) and ^9 (upper
    // band) so it visually layers ABOVE the orange twilight band but
    // BELOW the upper Rayleigh blue.
    float ozoneBand = pow(edgeFactor, 4.5) - pow(edgeFactor, 9.0);
    ozoneBand = max(ozoneBand, 0.0);
    vec3 ozoneTint = vec3(0.55, 0.40, 0.95);
    finalColor += ozoneTint * ozoneBand * dayFactor * 0.55 * smoothstep(0.0, 0.5, rayleighStrengthMul);

    // Night-side limb glow. Apollo / ISS imagery of Earth's dark side
    // shows a thin luminous ribbon at the silhouette: orange-yellow at
    // the very base (forward-scattered sun + airglow), blending into
    // blue higher up (high-altitude Rayleigh on the day-side beyond
    // the terminator that the line of sight grazes through). Without
    // this rim the night side hard-cuts at the limb and reads as a
    // black silhouette against space.
    //
    // Only the EARTH atmosphere has rayleighStrengthMul > 0 in the
    // sky-blue range, so we gate this glow by that strength so other
    // dim atmospheres (Mars / Venus / Titan) don't get a phantom
    // Earth-blue night rim.
    float nightFactor = 1.0 - dayFactor;
    // Night-limb visibility is distance-honest: from afar the dark
    // limb shows nothing except a thin scatter ring hugging the
    // terminator (airglow is far too faint to see at distance), but
    // from close range (low orbit) the airglow band genuinely
    // resolves along the whole night limb, as ISS photography shows.
    // closeFactor is the existing 5R→2R proximity ramp.
    float twilightProximity = smoothstep(-0.45, -0.02, sunDot);
    float nightLimbVis = max(twilightProximity, closeFactor * 0.85);
    float nightLimb = fresnel * nightFactor * smoothstep(0.0, 0.5, rayleighStrengthMul) * nightLimbVis;
    // Vertical fade across the rim band: orange at the very base
    // (close to the planet silhouette, edgeFactor near 1), fading
    // to blue as we move out into the upper atmosphere.
    float baseSlice = pow(edgeFactor, 12.0);  // very tight band at base
    float upperSlice = pow(edgeFactor, 4.5) - baseSlice;
    vec3 nightOrange = vec3(1.0, 0.55, 0.25);
    vec3 nightBlue   = vec3(0.35, 0.55, 0.95);
    finalColor += (nightOrange * baseSlice * 0.55 + nightBlue * upperSlice * 0.30) * nightLimb;

    // Backlit forward-scatter ring (Venus-class atmospheres). When
    // the sun sits behind the planet relative to the camera
    // (cosTheta strongly negative), light grazing the limb scatters
    // forward through the dense atmosphere and the entire night-side
    // silhouette lights up as a thin ring — the "ring of fire" of
    // Venus near inferior conjunction. Deliberately NOT gated by
    // litFactor: this is exactly the light that skims PAST the
    // planet's silhouette, which the shadow-cone test would wrongly
    // kill at the limb. Tight fresnel band so it reads as a ring,
    // not a glow shell.
    float ringAlpha = 0.0;
    if (forwardRingStrength > 0.001) {
      float backlit = smoothstep(0.25, 0.85, -cosTheta);
      float ringBand = pow(edgeFactor, 7.0);
      vec3 ringTint = mix(twiTint, vec3(1.0, 0.97, 0.90), 0.45);
      float ringTerm = ringBand * backlit * forwardRingStrength;
      finalColor += ringTint * ringTerm * 1.6;
      ringAlpha = ringTerm * 0.9;
    }

    // Alpha driven by sun-facing fraction. Night-side trace floor
    // dropped from 0.02 → 0.0 — the night atmosphere should be
    // essentially invisible across the disk, not a faint blue dome.
    // Only the dedicated night-limb ribbon (nightAlpha, gated by
    // nightLimb which is itself fresnel × nightFactor) survives on
    // the dark side. The thin orange/blue rim at the silhouette
    // stays visible because nightAlpha rises sharply near the limb.
    // Knocked nightAlpha multipliers down a touch (0.6→0.45 base,
    // 0.35→0.25 upper) so even the rim ribbon is subtle.
    // Soft outer taper: at the absolute silhouette (edgeFactor → 1)
    // the alpha is rolled back so the visible band peaks just
    // INSIDE the silhouette rather than AT it. The mesh edge then
    // fades into space over a wider band instead of cutting hard.
    // Baseline (orbital) now starts at edgeFactor 0.85 and reaches
    // 78% taper depth — the same diffuse character the user liked
    // at ISS-close applied as the default. ISS view pushes it
    // slightly further (0.78 start, 88% depth) so very-close-up
    // gets the most aggressive feather.
    float taperStart = mix(0.85, 0.78, issLevelFactor);
    float taperDepth = mix(0.78, 0.88, issLevelFactor);
    float outerTaper = 1.0 - smoothstep(taperStart, 1.0, edgeFactor) * taperDepth;
    if (earthDensityLimb > 0.5) {
      // Fall off with the ray's actual altitude, rather than leaving a
      // nonzero Fresnel ring at the shell mesh's silhouette. Cross-product
      // impact distance avoids subtracting two large squared distances.
      vec3 ray = normalize(vPosition);
      float impact = length(cross(-vPlanetViewCenter, ray));
      float altitude = max(0.0, impact - planetRadius);
      float scaleHeight = planetRadius * (8000.0 / 6371000.0);
      float thickness = max(atmosphereRadius - planetRadius, scaleHeight);
      float column = 6.0 * exp(-altitude / scaleHeight);
      float edgeFade = 1.0 - smoothstep(thickness * 0.65, thickness, altitude);
      outerTaper = (1.0 - exp(-column)) * edgeFade;
    }
    float dayAlpha   = fresnel * atmosphereStrength * dayFactor * closeHazeBoost * outerTaper;
    // Extra wide haze alpha contribution (separate from dayAlpha so
    // it isn't dampened by outerTaper). Carries the soft glow into
    // the outer band where dayAlpha alone would be near zero.
    float hazeAlpha = softHaze * atmosphereStrength * dayFactor * 0.18 * outerTaper;
    float nightAlpha = (baseSlice * 0.45 + upperSlice * 0.25) * nightLimb * atmosphereStrength;
    // Aurora gets its own alpha lane so the polar green doesn't get
    // gated down by the night-side trace floor — without this the
    // green is in finalColor but the surrounding alpha kills it.
    float alpha = max(max(max(max(dayAlpha, nightAlpha), auroraAlpha), hazeAlpha), ringAlpha);
    alpha = smoothstep(0.0, 1.0, alpha) * 0.7;
    if (earthDensityLimb > 0.5) {
      // Apply column density once. Repeated smoothstep/alpha products
      // compress an otherwise broad optical falloff into another thin rim.
      float earthDay = fresnel * atmosphereStrength * dayFactor * 0.65;
      alpha = clamp(max(max(earthDay, nightAlpha), auroraAlpha) * outerTaper, 0.0, 0.85);
    }

    gl_FragColor = vec4(finalColor, alpha * (1.0 - uShellCloseFade));
  }
`;

// Simple glow shader for planets without full atmosphere
export const simpleGlowVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const simpleGlowFragmentShader = `
  uniform vec3 glowColor;
  uniform float coefficient;
  uniform float power;

  varying vec3 vNormal;
  varying vec3 vPosition;

  void main() {
    vec3 viewDir = normalize(-vPosition);
    float intensity = pow(coefficient - dot(viewDir, vNormal), power);
    gl_FragColor = vec4(glowColor, intensity * 0.5);
  }
`;
